import React from 'react';
import { Group, IconButton, Menu, MenuProps, Shortcut, useToast } from '@economic/taco';
import { t } from '@lingui/macro';
import FilterInput from '~/app/shared/components/FilterInput';
import exportToExcel, { Columns, unpaginatedExportToExcel } from '~/app/shared/util/utilities/exportToExcel';
import { SourceComponent } from '~/types';

export interface FiltersAndActionsPropsBase {
    sourceComponent?: SourceComponent;
    hideImport?: boolean;
    hideExport?: boolean;
    hidePrint?: boolean;
    hideAdvancedSearch?: boolean;
    hideSearchIcon?: boolean;
    hasAccessAutoAttach?: boolean;
    isAdvancedSearchApplied?: boolean;
    onShowAdvancedSearch?: () => void;
    handleOnAfterPrint?: () => void;
    handleOnPrint?: () => void;
    exporter?: {
        url: string;
        filename: string;
        data: any[];
        headlines: any[];
        columns: { accessor: string; columnHeader?: string }[];
    };
    importer?: {
        menuProps?: Partial<MenuProps>;
        menuItems: JSX.Element[];
    };
    importAction?: () => void;
    excelExporter?: {
        basePath: string;
        dataEndpoint: string;
        exportEndpoint: string;
        filename: string;
        headlines: string[];
        columns?: Columns;
        onExport?: () => void;
    };
    collapsed?: boolean;
    onCollapsed?: (collapsed: boolean) => void;
    onShowImportDocsFromInbox?: () => void;
    onShowAutoAttach?: () => void;
}

type FilterProps =
    | {
          hideFilter?: false;
          filter: string;
          onFilter: (filter: string) => void;
      }
    | {
          hideFilter: true;
      };

type FiltersAndActionsProps = FiltersAndActionsPropsBase & FilterProps;

const FiltersAndActions = ({
    sourceComponent,
    exporter,
    importer,
    importAction,
    excelExporter,
    onShowAdvancedSearch,
    isAdvancedSearchApplied,
    onCollapsed,
    hideImport = false,
    hideExport = false,
    hidePrint = false,
    hideSearchIcon = false,
    hideAdvancedSearch = false,
    collapsed = false,
    onShowImportDocsFromInbox,
    onShowAutoAttach,
    handleOnAfterPrint,
    handleOnPrint,
    hasAccessAutoAttach,
    ...otherProps
}: FiltersAndActionsProps) => {
    const toast = useToast();
    window.onafterprint = () => {
        if (handleOnAfterPrint) {
            handleOnAfterPrint();
        }
    };
    const handlePrint = () => (handleOnPrint ? handleOnPrint() : window.print());
    const handleExport = () => {
        exporter && exportToExcel(exporter.url, exporter.filename, exporter.data, exporter.headlines, toast, exporter.columns);
        excelExporter &&
            unpaginatedExportToExcel(
                excelExporter.basePath,
                excelExporter.dataEndpoint,
                excelExporter.exportEndpoint,
                excelExporter.filename,
                excelExporter.headlines,
                toast,
                excelExporter.columns
            );

        excelExporter?.onExport && excelExporter.onExport();
    };

    return (
        <Group>
            {onShowImportDocsFromInbox && (
                <IconButton
                    aria-label={t`Drag and drop documents from inbox`}
                    tooltip={t`Drag and drop documents from inbox`}
                    icon="document-create-entry"
                    onClick={onShowImportDocsFromInbox}
                />
            )}
            {onShowAutoAttach && hasAccessAutoAttach && (
                <IconButton
                    aria-label={t`Auto attach documents to draft entries`}
                    tooltip={t`Auto attach documents to draft entries`}
                    icon="attach-auto"
                    onClick={onShowAutoAttach}
                />
            )}
            {onCollapsed && (
                <IconButton
                    aria-label={collapsed ? t`Show more columns` : t`Show fewer columns`}
                    tooltip={collapsed ? t`Show more columns` : t`Show fewer columns`}
                    icon={collapsed ? 'show-more' : 'show-less'}
                    onClick={() => onCollapsed(!collapsed)}
                />
            )}
            {!hideImport && (importer || importAction) && (
                <IconButton
                    tooltip={t`Import`}
                    aria-label={t`Import`}
                    icon="import"
                    onClick={importAction}
                    menu={
                        importer
                            ? menuProps => (
                                  <Menu {...(importer.menuProps ? importer.menuProps && menuProps : menuProps)}>
                                      <Menu.Content>{importer.menuItems}</Menu.Content>
                                  </Menu>
                              )
                            : undefined
                    }
                />
            )}
            {!hideExport && (
                <IconButton
                    aria-label={t`Export to Excel`}
                    tooltip={t`Export to Excel`}
                    icon="export-to-excel"
                    onClick={handleExport}
                />
            )}
            {!hidePrint && <IconButton aria-label={t`Print`} tooltip={t`Print`} icon="print" onClick={handlePrint} />}
            {!otherProps.hideFilter && (
                <FilterInput
                    sourceComponent={sourceComponent}
                    filter={otherProps.filter}
                    onFilter={otherProps.onFilter}
                    hideIcon={hideSearchIcon}
                />
            )}
            {!hideAdvancedSearch && (
                <IconButton
                    tooltip={
                        <>
                            {t`Advanced search`}
                            {sourceComponent === SourceComponent.ACTable && (
                                <Shortcut className="ml-2" keys={{ key: 'f', meta: true, shift: true }} />
                            )}
                        </>
                    }
                    aria-label={t`Advanced search`}
                    icon={isAdvancedSearchApplied ? 'filter-solid' : 'filter'}
                    onClick={onShowAdvancedSearch}
                />
            )}
        </Group>
    );
};

export default FiltersAndActions;
